import { buildApiUrl, fetchJSON, validateRequiredParams } from '../crm/crmApi';

export type MarginType = 'percent' | 'absolute';

export interface CatalogItemInput {
  organization_id: string;
  supplier_id: string;
  name: string;
  description?: string;
  category?: string;
  sku?: string;
  base_cost: number;
  cost_currency?: string;
  default_margin_type?: MarginType;
  default_margin_value?: number;
  status?: 'active' | 'inactive' | 'archived';
  metadata?: Record<string, unknown>;
}

export interface CatalogItem extends CatalogItemInput {
  id: string;
  status: 'active' | 'inactive' | 'archived';
  default_margin_type: MarginType;
  default_margin_value: number;
  cost_currency: string;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ListCatalogParams {
  organization_id: string;
  supplier_id?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface SolutionItemInput {
  catalog_item_id?: string;
  supplier_id: string;
  name: string;
  description?: string;
  quantity: number;
  unit_cost: number;
  unit_price?: number;
  margin_type?: MarginType;
  margin_value?: number;
  position?: number;
  metadata?: Record<string, unknown>;
}

export interface SolutionTemplateInput {
  organization_id: string;
  name: string;
  description?: string;
  status?: 'draft' | 'published' | 'archived';
  margin_lock?: boolean;
  currency?: string;
  source_quote_id?: string | null;
  metadata?: Record<string, unknown>;
  items: SolutionItemInput[];
}

export interface SolutionTemplate extends SolutionTemplateInput {
  id: string;
  margin_lock: boolean;
  total_estimated_cost: number;
  total_estimated_price: number;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface SolutionTemplateWithItems {
  data: SolutionTemplate;
  items: Array<SolutionItemInput & { id: string } & { unit_price: number }>;
}

export interface ListTemplatesParams {
  organization_id: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

const supplierSolutionsService = {
  async listCatalog(params: ListCatalogParams): Promise<{ data: CatalogItem[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'supplierSolutions.listCatalog'
    });
    const url = buildApiUrl('/api/supplier-solutions/catalog', params as any);
    return fetchJSON(url, {
      description: 'List supplier solution catalog items',
      context: {
        organization_id: params.organization_id,
        supplier_id: params.supplier_id,
        status: params.status,
        search: params.search,
        limit: params.limit,
        offset: params.offset
      }
    });
  },

  async getCatalog(id: string, organization_id: string): Promise<{ data: CatalogItem | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierSolutions.getCatalog'
    });
    const url = buildApiUrl(`/api/supplier-solutions/catalog/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get supplier solution catalog item',
      context: { id, organization_id }
    });
  },

  async createCatalog(payload: CatalogItemInput): Promise<{ data: CatalogItem }>{
    validateRequiredParams(payload, ['organization_id', 'supplier_id', 'name', 'base_cost'], {
      service: 'supplierSolutions.createCatalog'
    });
    const url = buildApiUrl('/api/supplier-solutions/catalog');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create supplier solution catalog item',
      context: { organization_id: payload.organization_id, supplier_id: payload.supplier_id }
    });
  },

  async updateCatalog(id: string, payload: Partial<CatalogItemInput> & { organization_id: string }): Promise<{ data: CatalogItem }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'supplierSolutions.updateCatalog'
    });
    const url = buildApiUrl(`/api/supplier-solutions/catalog/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update supplier solution catalog item',
      context: { id, organization_id: payload.organization_id }
    });
  },

  async removeCatalog(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierSolutions.removeCatalog'
    });
    const url = buildApiUrl(`/api/supplier-solutions/catalog/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete supplier solution catalog item',
      context: { id, organization_id }
    });
  },

  async listTemplates(params: ListTemplatesParams): Promise<{ data: SolutionTemplate[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'supplierSolutions.listTemplates'
    });
    const url = buildApiUrl('/api/supplier-solutions/templates', params as any);
    return fetchJSON(url, {
      description: 'List supplier solution templates',
      context: {
        organization_id: params.organization_id,
        status: params.status,
        search: params.search,
        limit: params.limit,
        offset: params.offset
      }
    });
  },

  async getTemplate(id: string, organization_id: string): Promise<SolutionTemplateWithItems | null>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierSolutions.getTemplate'
    });
    const url = buildApiUrl(`/api/supplier-solutions/templates/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get supplier solution template',
      context: { id, organization_id }
    });
  },

  async createTemplate(payload: SolutionTemplateInput): Promise<SolutionTemplateWithItems>{
    validateRequiredParams(payload, ['organization_id', 'name'], {
      service: 'supplierSolutions.createTemplate'
    });
    if (!payload.items || payload.items.length === 0) {
      throw new Error('At least one solution item is required');
    }
    const url = buildApiUrl('/api/supplier-solutions/templates');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create supplier solution template',
      context: { organization_id: payload.organization_id }
    });
  },

  async updateTemplate(id: string, payload: Partial<SolutionTemplateInput> & { organization_id: string }): Promise<SolutionTemplateWithItems>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'supplierSolutions.updateTemplate'
    });
    const url = buildApiUrl(`/api/supplier-solutions/templates/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update supplier solution template',
      context: { id, organization_id: payload.organization_id }
    });
  },

  async removeTemplate(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierSolutions.removeTemplate'
    });
    const url = buildApiUrl(`/api/supplier-solutions/templates/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete supplier solution template',
      context: { id, organization_id }
    });
  },

  async downloadCsvTemplate(): Promise<void> {
    const url = buildApiUrl('/api/supplier-solutions/catalog/csv-template');
    const { supabase } = await import('../../config/supabaseClient');
    const sessionResp = await supabase.auth.getSession();
    const token = (sessionResp as any)?.data?.session?.access_token;

    if (!token) {
      throw new Error('Authentication required to download CSV template');
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download CSV template');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'catalog-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  async bulkUploadCatalog(file: File, organization_id: string): Promise<{ inserted: number; errors: Array<{ rowNumber: number; message: string }> }> {
    validateRequiredParams({ organization_id }, ['organization_id'], {
      service: 'supplierSolutions.bulkUploadCatalog'
    });

    const { supabase } = await import('../../config/supabaseClient');
    const sessionResp = await supabase.auth.getSession();
    const token = (sessionResp as any)?.data?.session?.access_token;
    const userResp = await supabase.auth.getUser();
    const userId = (userResp as any)?.data?.user?.id;

    if (!token || !userId) {
      throw new Error('Authentication required to upload CSV');
    }

    const url = buildApiUrl('/api/supplier-solutions/catalog/bulk-upload');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organization_id);
    formData.append('userId', userId);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload CSV');
    }

    return response.json();
  },

  async duplicateTemplate(id: string, organization_id: string, newName?: string): Promise<{ data: SolutionTemplate }> {
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierSolutions.duplicateTemplate'
    });

    // First fetch the original template
    const original = await this.getTemplate(id, organization_id);
    if (!original?.data) {
      throw new Error('Template not found');
    }

    // Create duplicate with modified name and draft status
    const duplicateData: SolutionTemplateInput = {
      organization_id,
      name: newName || `${original.data.name} (Copy)`,
      description: original.data.description,
      status: 'draft',
      margin_lock: original.data.margin_lock,
      items: original.data.items || []
    };

    return this.createTemplate(duplicateData);
  }
};

export default supplierSolutionsService;
